// Settings → Organisation (the main admin only): "Download all our data" and "Close organisation".
import { badge, closeModal, esc, formatDateTime, formError, formValues, get, openModal, post, toast } from "../lib.js";
import { onAction, onForm, refreshSession, rerender, state } from "../state.js";

interface ExportRow { id: string; status: string; requestedBy: string; mine: boolean; size: string; createdAt: string; readyAt: string | null; expiresAt: string | null; downloads: number; error: string }

let timer = 0;
const STATUS: Record<string, [string, "green" | "amber" | "red" | "gray"]> = { building: ["Preparing…", "amber"], ready: ["Ready", "green"], failed: ["Failed", "red"], expired: ["Expired", "gray"] };

function exportsHtml(list: ExportRow[]): string {
  if (!list.length) return '<p class="muted">No downloads yet.</p>';
  return `<div class="table-wrap"><table class="od-exports"><thead><tr><th>Asked for</th><th>By</th><th>Status</th><th>Size</th><th>Link works until</th><th></th></tr></thead><tbody>
${list.map((r) => `<tr><td>${esc(formatDateTime(r.createdAt))}</td><td>${esc(r.requestedBy)}</td><td>${badge(STATUS[r.status]?.[0] || r.status, STATUS[r.status]?.[1] || "gray")}${r.status === "failed" && r.error ? `<br><small class="muted">${esc(r.error)}</small>` : ""}</td><td>${esc(r.size || "—")}</td>
<td>${r.status === "ready" && r.expiresAt ? esc(formatDateTime(r.expiresAt)) : "—"}</td>
<td>${r.status === "ready" && r.mine ? `<a class="primary small-btn" href="/api/org/data-export/${esc(r.id)}/download" download>Download .zip</a>` : r.status === "ready" ? '<small class="muted">Only the admin who asked can download it.</small>' : ""}</td></tr>`).join("")}
</tbody></table></div>`;
}

/** The two panels, added at the end of Settings → Organisation for the main admin. */
export function orgDataPanelsHtml(): string {
  const org = state.session.organisation;
  if (!org?.mainAdmin || state.session.supportAccess) return "";
  const closed = org.closed;
  return `<div class="panel" id="data"><div class="panel-head"><div><h3>Download all our data</h3><span class="muted">One .zip file with a spreadsheet (CSV) of every kind of record, every uploaded file and photo, and a README. Passwords, two-factor secrets and encryption keys are never included. It is prepared in the background; you get a notice and an email when it's ready. The link works for 7 days, only for you.</span></div>
<button type="button" class="primary" data-action="od-export">Download all our data</button></div>
<div id="od-exports">${'<p class="muted">Loading…</p>'}</div></div>
<div class="panel od-close"><div class="panel-head"><div><h3>Close organisation</h3><span class="muted">${closed
    ? `Closed on ${esc(formatDateTime(closed.at))} by ${esc(closed.by)}. Read-only until ${esc(formatDateTime(closed.deleteAfter))}; after that Falcon can delete it permanently. To reopen it, contact Falcon support.`
    : "Closing makes the organisation read-only for 30 days: everyone can still sign in, look at everything and you can download all the data, but nothing can be changed. After 30 days Falcon can delete it permanently. Falcon can reopen it during the 30 days."}</span></div>
${closed ? badge("Closed", "red") : `<button type="button" class="danger" data-action="od-close">Close organisation</button>`}</div></div>`;
}

/** Fill in the downloads list (and keep it up to date while one is being prepared). */
export async function loadOrgData(): Promise<void> {
  window.clearTimeout(timer);
  const holder = document.getElementById("od-exports");
  if (!holder) return;
  try {
    const list = await get<ExportRow[]>("/api/org/data-export");
    if (!holder.isConnected) return;
    holder.innerHTML = exportsHtml(list);
    if (list.some((r) => r.status === "building")) timer = window.setTimeout(() => void loadOrgData(), 4000);
    if (location.hash === "#data") document.getElementById("data")?.scrollIntoView({ block: "start" });
  } catch (err) {
    holder.innerHTML = `<p class="muted">${esc((err as Error).message)}</p>`;
  }
}

onAction("od-export", async (el) => {
  (el as HTMLButtonElement).disabled = true;
  try {
    const r = await post<ExportRow & { alreadyBuilding: boolean }>("/api/org/data-export", {});
    toast(r.alreadyBuilding ? "A download is already being prepared. You'll get a notice when it's ready." : "Preparing your download. You'll get a notice and an email when it's ready.");
    await loadOrgData();
  } catch (err) {
    toast((err as Error).message, "error");
  } finally {
    (el as HTMLButtonElement).disabled = false;
  }
});

onAction("od-close", () => {
  const name = state.session.organisation?.name || "";
  openModal("Close organisation", `
<form data-form="od-close" class="form-grid" novalidate>
  <div class="form-message" role="alert" hidden></div>
  <div class="form-group full master-admin-note danger-note"><strong>${esc(name)} becomes read-only for 30 days</strong><span>Everyone can still sign in and look at everything, and you can download all the data, but nothing can be changed. After 30 days Falcon can delete the organisation permanently, with all its records and files. Only Falcon can reopen it.</span></div>
  <div class="form-group full"><label>Type the organisation's name to confirm: <strong>${esc(name)}</strong></label><input name="confirmName" required autocomplete="off"></div>
  <div class="form-group full form-actions"><button type="button" class="secondary" data-action="close-modal">Cancel</button><button type="submit" class="danger">Close organisation</button></div>
</form>`);
});
onForm("od-close", async (form) => {
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  btn.disabled = true;
  try {
    await post("/api/org/closing/close", { confirmName: formValues(form).confirmName });
    closeModal();
    toast("The organisation is closed and read-only for 30 days.");
    await refreshSession();
    await rerender();
  } catch (err) {
    formError(form, err);
  } finally {
    btn.disabled = false;
  }
});
